/**
 * Combined-slip tyre: one friction circle shared by driving / braking and cornering.
 *
 * The slip ratio and the slip angle are each normalised by their peak, s = hypot(k / kPeak, tan(a) / tan(aPeak)),
 * and one Pacejka-like curve F = mu*N*sin(C*atan(B*s)) gives the total force, split along the slip direction.
 * B = tan(pi / 2C) puts the peak exactly at s = 1; far past it the force settles at sin(C*pi/2) of the peak
 * (sliding friction). So a wheel that is spinning or locked has little left for cornering, and a tyre at its
 * cornering limit has nothing left for drive or brakes - the coach's "throttle at lean steps the rear out" and
 * "front brake at lean washes the front" both come from this.
 */
export interface TyreParams { slipPeak: number; anglePeak: number; shape: number }
export interface TyreForce {
  fx: number;    // N along the wheel heading, + = forward
  fy: number;    // N sideways, opposes the slip
  usage: number; // normalised combined slip; > 1 means past the peak (sliding)
  dFxdSlip: number; // d fx / d slipRatio at this point (N per unit slip ratio), for implicit wheel integration
}

/**
 * Writes the force into `out` (reused by the 240 Hz wheel step: no allocation) and returns it; without `out` a new
 * object is made (tests, tools).
 */
export function tyreForce(slipRatio: number, tanSlipAngle: number, muN: number, p: TyreParams,
  out: TyreForce = { fx: 0, fy: 0, usage: 0, dFxdSlip: 0 }): TyreForce {
  const sx = slipRatio / p.slipPeak;
  const sy = tanSlipAngle / Math.tan(p.anglePeak);
  const s = Math.sqrt(sx * sx + sy * sy);
  const b = Math.tan(Math.PI / (2 * p.shape));
  out.usage = s;
  if (muN <= 0) { out.fx = 0; out.fy = 0; out.dFxdSlip = 0; return out; }
  if (s < 1e-12) { out.fx = 0; out.fy = 0; out.dFxdSlip = (muN * p.shape * b) / p.slipPeak; return out; }
  const a = p.shape * Math.atan(b * s);
  const f = muN * Math.sin(a);
  const df = (muN * Math.cos(a) * p.shape * b) / (1 + b * b * s * s); // dF/ds
  // fx = F(s) sx / s: d/dsx = F'(s) (sx/s)^2 + F(s) sy^2 / s^3.
  const dFxdSx = df * (sx / s) * (sx / s) + (f * sy * sy) / (s * s * s);
  out.fx = (f * sx) / s; out.fy = (-f * sy) / s; out.dFxdSlip = dFxdSx / p.slipPeak;
  return out;
}
